import React, { useEffect, useState } from "react";
import { useContent } from "../lib/ContentContext";

const SECTION_IDS = [
  { id: "start", key: "subnav_inicio" },
  { id: "cenarios", key: "subnav_cenarios" },
  { id: "elenco", key: "subnav_elenco" },
  { id: "cortes", key: "subnav_cortes" },
  { id: "bagagem", key: "subnav_bagagem" },
  { id: "agenda", key: "subnav_agenda" },
];

/**
 * Sticky sub-navigation for the guest logged area.
 * Sits below the main Nav (which is 80px tall). Highlights the current
 * section using IntersectionObserver + smooth scrolls on click.
 * The "Confirmar" item is visually separated as a primary CTA.
 * Labels are pulled from content.guest.subnav_* so they're CMS-editable.
 */
export default function GuestSubNav({ onConfirm }) {
  const { content } = useContent();
  const g = content.guest;
  const sections = SECTION_IDS.map((s) => ({ id: s.id, label: g[s.key] }));
  const [active, setActive] = useState("start");
  const [elevated, setElevated] = useState(false);

  // Elevation shadow when the page is scrolled past the top hero
  useEffect(() => {
    const onScroll = () => setElevated(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Track active section via IntersectionObserver
  useEffect(() => {
    const ids = [...sections.map((s) => s.id), "rsvp"];
    const elements = ids
      .map((id) => document.getElementById(id))
      .filter(Boolean);
    if (!elements.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        // Pick the entry closest to the top that is intersecting
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length) setActive(visible[0].target.id);
      },
      {
        // Trigger when a section crosses the sticky bar (nav 80 + subnav 56)
        rootMargin: "-160px 0px -55% 0px",
        threshold: 0,
      }
    );

    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const jumpTo = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    // Account for sticky nav+subnav height (~136px)
    const y = el.getBoundingClientRect().top + window.scrollY - 136;
    window.scrollTo({ top: y, behavior: "smooth" });
  };

  const handleConfirm = () => {
    if (onConfirm) onConfirm();
    else jumpTo("rsvp");
  };

  return (
    <div
      data-testid="guest-subnav"
      className={`sticky top-20 z-40 bg-[#F9F8F6] border-b border-neutral-200 transition-shadow duration-300 ${
        elevated ? "shadow-[0_4px_18px_rgba(0,0,0,0.06)]" : ""
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-4 md:px-12">
        <div className="flex items-center justify-between gap-4 h-14">
          {/* Left: section pills */}
          <div className="flex-1 min-w-0 overflow-x-auto no-scrollbar">
            <div className="flex items-center gap-1 md:gap-2 min-w-max">
              {sections.map((s) => {
                const isActive = active === s.id;
                return (
                  <button
                    key={s.id}
                    onClick={() => jumpTo(s.id)}
                    data-testid={`subnav-${s.id}`}
                    data-active={isActive ? "true" : "false"}
                    className={`relative px-3 md:px-4 py-4 mono text-[12px] md:text-[13px] uppercase tracking-[0.14em] transition-colors whitespace-nowrap ${
                      isActive
                        ? "text-[color:var(--alta-red)] font-semibold"
                        : "text-neutral-500 hover:text-neutral-900"
                    }`}
                  >
                    {s.label}
                    <span
                      className={`absolute bottom-0 left-2 right-2 h-[3px] rounded-t-sm transition-all duration-300 ${
                        isActive
                          ? "bg-[color:var(--alta-red)] opacity-100"
                          : "opacity-0"
                      }`}
                    />
                  </button>
                );
              })}
            </div>
          </div>

          {/* Divider */}
          <span className="hidden md:block h-6 w-px bg-neutral-300 shrink-0" />

          {/* Right: Confirm CTA */}
          <button
            onClick={handleConfirm}
            data-testid="subnav-confirmar"
            data-active={active === "rsvp" ? "true" : "false"}
            className={`shrink-0 mono text-[11px] md:text-[12px] uppercase tracking-[0.16em] px-4 md:px-5 py-2.5 border transition-colors ${
              active === "rsvp"
                ? "bg-[color:var(--alta-red)] border-[color:var(--alta-red)] text-[#F9F8F6]"
                : "bg-[color:var(--alta-red)] border-[color:var(--alta-red)] text-[#F9F8F6] hover:bg-[#8f1f17] hover:border-[#8f1f17]"
            }`}
          >
            {g.subnav_confirmar || "Confirmar vaga →"}
          </button>
        </div>
      </div>
    </div>
  );
}
